import { useEffect, useRef, useState } from 'react';
import { Link } from './LocaleLink';
import { applyConsent, initStoredConsent, onConsentReprompt, type ConsentChoice } from '../utils/consent';
import { useLang } from '../utils/i18n';

/**
 * Bottom-of-screen consent banner.
 *
 * Renders nothing until after mount, for two reasons: localStorage does not
 * exist during the SSG prerender, and rendering the banner on the server would
 * make the static HTML disagree with the client for anyone who already chose.
 *
 * Neither button is pre-selected and neither is focused by default — "Accept
 * All" carries no visual or behavioural head start over "Essential Only".
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(true); // Always render initially for SSR to eliminate LCP delay
  const bannerRef = useRef<HTMLDivElement>(null);
  const { t } = useLang();

  useEffect(() => {
    // Replays a stored choice (hides banner) or reveals the banner on a first visit.
    if (initStoredConsent()) setVisible(false);
    // Re-opens when the footer's "Cookie settings" control is used.
    return onConsentReprompt(() => setVisible(true));
  }, []);

  // Reserve translated banner height on phones so wheel controls are never
  // hidden behind the consent choice.
  useEffect(() => {
    if (!visible) return;
    const root = document.documentElement;
    const update = () => {
      const height = bannerRef.current?.getBoundingClientRect().height ?? 0;
      root.style.setProperty('--cookie-banner-height', `${height}px`);
    };
    root.classList.add('cookie-banner-visible');
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (bannerRef.current) observer?.observe(bannerRef.current);
    window.addEventListener('resize', update, { passive: true });
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      root.classList.remove('cookie-banner-visible');
      root.style.removeProperty('--cookie-banner-height');
    };
  }, [visible, t]);

  const choose = (choice: ConsentChoice) => {
    applyConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      id="cookie-banner-container"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-text"
      className="fixed top-12 bottom-auto sm:top-auto sm:bottom-0 inset-x-0 z-50 border-y sm:border-t sm:border-b-0 border-gray-200 dark:border-gray-700 bg-white/98 dark:bg-gray-900/98 backdrop-blur shadow-lg sm:shadow-[0_-4px_24px_rgba(0,0,0,0.10)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
        <p id="cookie-banner-text" className="flex-1 text-xs sm:text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">
          {t('chrome.cookieMessage')}{' '}
          <Link
            to="/privacy/"
            className="text-violet-600 dark:text-violet-400 font-semibold hover:underline whitespace-nowrap"
          >
            {t('chrome.privacyPolicy')}
          </Link>
        </p>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => choose('denied')}
            className="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-[13px] font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {t('chrome.essentialOnly')}
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className="flex-1 sm:flex-none px-5 py-2 text-xs sm:text-[13px] font-bold rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow-sm shadow-violet-600/25"
          >
            {t('chrome.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
}
